import taxonomy from '@/lib/artistTaxonomy.json';
import ArtCategorySearch from '@/components/ArtCategorySearch';
import { artFilterTerms, normalizeArtSearch, taxonomyEntries } from '@/lib/taxonomySearch';
import { useState, useEffect, useMemo } from 'react';
import usePrerenderReady from '@/lib/usePrerenderReady';
import RegisteredArtistCard from '@/components/RegisteredArtistCard';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useLanguage } from '@/context/LanguageContext';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { analyzeArtistProfile } from '@/lib/profileCoach';
import usePageContent from '@/lib/usePageContent';
import { artistCategoryOptions, splitArtValues } from '@/lib/artistCategories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const slugifyFilter = (value = '') =>
  String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const uniqueSorted = (items = []) =>
  Array.from(new Set(items.map((item) => String(item || '').trim()).filter(Boolean))).sort((a, b) => a.localeCompare(b));

const normalizeArtKey = (value = '') => String(value || '').trim().toLowerCase();

const getArtistArtValues = (artist = {}) =>
  uniqueSorted([
    ...splitArtValues(artist.category),
    ...splitArtValues(artist.subcategory),
    artist.display_profession,
  ]);

const getArtistMainCategories = (artist = {}) => splitArtValues(artist.category);
const getArtistSubcategories = (artist = {}) => splitArtValues(artist.subcategory);

const getGroupTerms = (group = {}) => {
  const configuredSubcategories = (group.categories || []).flatMap((category) => artistCategoryOptions[category] || []);
  return uniqueSorted([...(group.categories || []), ...configuredSubcategories, ...(group.examples || [])]);
};

const artistMatchesGroup = (artist = {}, group = {}) => {
  if (!group || group.id === 'all') return true;
  const artistTerms = new Set(getArtistArtValues(artist).map(normalizeArtKey));
  return getGroupTerms(group).some((term) => artistTerms.has(normalizeArtKey(term)));
};

const artTermCache = new Map();
const artistMatchesArtFilter = (artist = {}, filter = 'All') => {
  if (filter === 'All') return true;
  if (!artTermCache.has(filter)) artTermCache.set(filter, artFilterTerms(filter));
  return getArtistArtValues(artist).some(value => artTermCache.get(filter).has(normalizeArtSearch(value)));
};

const artistMatchesMainCategory = (artist = {}, category = 'All') =>
  category === 'All' || getArtistMainCategories(artist).some((value) => normalizeArtKey(value) === normalizeArtKey(category));

const CATEGORY_GROUP_IDS = [
  'all',
  'acting-performance',
  'dance',
  'music-vocals',
  'writing',
  'visual-media',
  'photography-film',
  'influencer-creator',
  'folk-cultural',
  'events-stage',
  'modelling-fashion', 'design-creative', 'beauty-body', 'education-culture', 'culinary-decorative',
];

const getDiscoveryRank = (artist = {}) => {
  const profileScore = analyzeArtistProfile(artist, { videoCount: artist.video_count || 0 }).score;
  const mediaBoost = (artist.image_url || artist.image ? 35 : 0)
    + (artist.youtube ? 25 : 0)
    + (artist.instagram ? 15 : 0)
    + (artist.spotify ? 15 : 0);
  const adminBoost = (artist.is_spotlight ? 1000 : 0)
    + (artist.is_featured ? 700 : 0)
    + (artist.is_verified ? 500 : 0);

  return adminBoost + (profileScore * 5) + mediaBoost;
};

const getFreshnessTime = (artist = {}) =>
  new Date(artist.updated_at || artist.created_at || 0).getTime() || 0;

const SEARCH_SYNONYMS = {
  watercolor: ['watercolour', 'painting'],
  watercolour: ['watercolor', 'painting'],
  painter: ['painting', 'visual arts'],
  sketch: ['sketching', 'drawing', 'pencil', 'charcoal'],
  '???': ['poet', 'poetry', 'writer', 'shayari', 'hindi poet'],
  '?????': ['poetry', 'poet', 'writer', 'spoken word'],
  '????': ['shayari', 'poet', 'urdu', 'poetry', 'mushaira'],
  '?????': ['shayari', 'urdu', 'poet', 'mushaira'],
  '????': ['singer', 'vocalist', 'music', 'singing'],
  '??????': ['singer', 'vocalist', 'music', 'singing'],
  '???': ['bhajan', 'devotional', 'singer', 'temple', 'kirtan'],
  '??? ????': ['bhajan', 'devotional singer', 'kirtan'],
  '??????': ['kirtan', 'bhajan', 'devotional singer'],
  '????': ['tabla', 'percussion', 'instrumentalist'],
  '????': ['dholak', 'percussion', 'folk', 'instrumentalist'],
  '?????': ['dancer', 'dance'],
  '?????': ['dance', 'dancer', 'choreographer'],
  '???': ['folk', 'regional', 'traditional'],
  '??????': ['folk singer', 'regional singer', 'traditional'],
  '???????': ['actor', 'acting', 'performer'],
  '????': ['anchor', 'host', 'emcee', 'stage host'],
  '????': ['sufi', 'qawwali', 'singer', 'music'],
  '???????': ['qawwali', 'sufi', 'singer', 'music'],
  '????????': ['painter', 'artist', 'visual artist'],
  '??????????': ['photographer', 'photography'],
  '?????': ['ghazal', 'urdu', 'singer', 'shayari'],
  singer: ['????', '??????', 'vocalist', 'singing'],
  poet: ['???', '?????', '????', 'shayari'],
  shayari: ['????', '?????', 'urdu', 'mushaira'],
  tabla: ['????', 'percussion'],
  bhajan: ['???', '??????', 'devotional'],
  anchor: ['????', 'host', 'emcee'],
  qawwali: ['???????', '????', 'sufi'],
  ghazal: ['?????', 'urdu', 'singer'],
  folk: ['???', '??????', 'regional', 'traditional'],
  painter: ['????????', 'visual artist'],
  photographer: ['??????????', 'photography'],
  tattoo: ['tattoo artist', 'tattoo, body & festival arts', 'temporary tattoo', 'body painting'],
  tattoos: ['tattoo', 'tattoo artist', 'tattoo, body & festival arts'],
  mehendi: ['henna', 'tattoo, body & festival arts', 'festival makeup'],
  henna: ['mehendi', 'tattoo, body & festival arts'],
  urdu: ['????', '?????', '?????', 'mushaira'],
  punjabi: ['??????', 'punjabi singer', 'bhangra', 'giddha'],
  bhojpuri: ['???????', 'folk', 'regional singer'],
  bengali: ['?????', 'rabindra sangeet', 'bengali poet'],
};

const SEARCH_FILLER_WORDS = new Set([
  'a', 'an', 'and', 'are', 'artist', 'artists', 'art', 'arts', 'available', 'best',
  'book', 'booking', 'find', 'for', 'hire', 'in', 'me', 'near', 'of', 'profile',
  'profiles', 'search', 'show', 'the',
]);

const expandSearchQuery = (query = '') => {
  const clean = query.trim().toLowerCase();
  if (!clean) return [];
  const tokens = clean.split(/\s+/).filter((token) => token && !SEARCH_FILLER_WORDS.has(token));
  const expanded = new Set([clean, ...tokens]);
  Object.entries(SEARCH_SYNONYMS).forEach(([key, values]) => {
    if (clean.includes(key.toLowerCase())) {
      values.forEach((value) => expanded.add(value.toLowerCase()));
    }
  });
  return Array.from(expanded).filter((token) => token && !SEARCH_FILLER_WORDS.has(token));
};

const parseArtistSearchQuery = (query = '') => {
  const raw = String(query || '').replace(/\s+/g, ' ').trim();
  const lower = raw.toLowerCase();
  const nearMe = /\bnear\s+me\b/.test(lower);
  let location = '';
  let artQuery = lower.replace(/\bnear\s+me\b/g, ' ');
  const locationMatch = artQuery.match(/\b(?:in|at|from|near)\s+([a-zA-Z][a-zA-Z\s.-]{1,60})$/);
  if (locationMatch) {
    location = locationMatch[1]
      .replace(/\b(artist|artists|art|arts|near|me|available|for|hire|booking)\b/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
    artQuery = artQuery.slice(0, locationMatch.index).trim();
  }

  const artTerms = artQuery
    .split(/\s+/)
    .filter((token) => token && !SEARCH_FILLER_WORDS.has(token))
    .join(' ');

  return {
    original: lower,
    artQuery: artTerms || artQuery.trim(),
    location,
    nearMe,
    tokens: expandSearchQuery(artTerms || artQuery.trim()),
  };
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const isUsableIndiaCoordinate = (latitude, longitude) =>
  Number.isFinite(latitude)
  && Number.isFinite(longitude)
  && latitude >= 6
  && latitude <= 38
  && longitude >= 68
  && longitude <= 98;

const getDistanceKm = (from, artist = {}) => {
  const lat1 = toNumber(from?.latitude);
  const lon1 = toNumber(from?.longitude);
  const lat2 = toNumber(artist.latitude);
  const lon2 = toNumber(artist.longitude);
  if ([lat1, lon1, lat2, lon2].some((value) => value === null)) return null;
  if (!isUsableIndiaCoordinate(lat1, lon1) || !isUsableIndiaCoordinate(lat2, lon2)) return null;
  const radians = (degrees) => degrees * (Math.PI / 180);
  const dLat = radians(lat2 - lat1);
  const dLon = radians(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLon / 2) ** 2;
  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const SOCIETY_EVENT_TERMS = [
  'society', 'community', 'ngo', 'school', 'college', 'cultural', 'festival',
  'devotional', 'bhajan', 'temple', 'welfare', 'social', 'charity', 'public',
  'independence day', 'republic day', 'diwali', 'holi', 'navratri', 'durga puja', 'ganesh',
  'wedding', 'sangeet', 'mehendi', 'corporate', 'college fest', 'open mic', 'heritage',
];

const SEASONAL_THEMES = [
  { label: 'Holi', query: 'holi folk dance rang barse music singer dhol' },
  { label: 'Navratri', query: 'navratri garba dandiya devotional dance singer' },
  { label: 'Diwali', query: 'diwali cultural singer dancer anchor rangoli' },
  { label: 'Independence Day', query: 'independence day patriotic singer dancer anchor poetry' },
  { label: 'Republic Day', query: 'republic day patriotic school college singer dance anchor' },
  { label: 'Durga Puja', query: 'durga puja devotional cultural singer dhaak folk' },
  { label: 'Ganesh Utsav', query: 'ganesh utsav devotional bhajan singer dhol tasha' },
  { label: 'Janmashtami', query: 'janmashtami krishna bhajan devotional dance singer' },
  { label: 'Eid & Mushaira', query: 'eid mushaira urdu poet qawwali sufi ghazal singer' },
  { label: 'Christmas', query: 'christmas choir carol singer band children school' },
  { label: 'Wedding Season', query: 'wedding sangeet mehendi singer anchor dancer band dj' },
  { label: 'College Fest', query: 'college fest youth singer rapper band dancer comedian anchor' },
  { label: 'Corporate Event', query: 'corporate event anchor comedian singer band motivational speaker' },
  { label: 'School Annual Day', query: 'school annual day dance teacher anchor singer theatre' },
  { label: 'Heritage Day', query: 'heritage folk cultural traditional classical dancer singer' },
  { label: 'Open Mic', query: 'open mic poet comedian singer acoustic rapper spoken word' },
];

const MOOD_DISCOVERY = [
  { label: 'Peaceful', query: 'peaceful soothing calm acoustic classical soft singer instrumental painter' },
  { label: 'Patriotic', query: 'patriotic independence day desh bhakti singer dancer anchor poetry' },
  { label: 'Spiritual', query: 'spiritual devotional bhajan sufi qawwali temple singer folk' },
  { label: 'Romantic', query: 'romantic love song poetry ghazal singer acoustic' },
  { label: 'Protest', query: 'protest social issue street theatre poet rapper spoken word' },
  { label: 'Wedding vibe', query: 'wedding sangeet mehendi singer anchor dancer dj band' },
  { label: 'Energetic', query: 'energetic high energy dj rapper dancer band youth college fest' },
  { label: 'Classical', query: 'classical kathak bharatanatyam hindustani carnatic instrumental vocalist' },
  { label: 'Folk & earthy', query: 'folk earthy regional traditional lok geet bihu lavani ghoomar' },
  { label: 'Sufi', query: 'sufi qawwali ghazal soulful devotional singer' },
  { label: 'Comedy', query: 'comedy comedian stand-up humorous anchor emcee' },
  { label: 'Luxury', query: 'premium luxury wedding corporate lounge acoustic jazz classical' },
  { label: 'Youthful', query: 'youth college fest hip hop rap reels indie band' },
  { label: 'Kids friendly', query: 'kids children school family magic puppet storyteller singer' },
  { label: 'Motivational', query: 'motivational speaker inspirational poetry corporate school college' },
  { label: 'Regional pride', query: 'regional language folk bhojpuri punjabi bengali marathi cultural' },
];

const parseSeasonalThemes = (value = '') =>
  String(value || '')
    .split(/\r?\n/)
    .map((line) => {
      const [label, ...queryParts] = line.split('|');
      const cleanLabel = String(label || '').trim();
      const cleanQuery = queryParts.join('|').trim();
      return cleanLabel && cleanQuery ? { label: cleanLabel, query: cleanQuery } : null;
    })
    .filter(Boolean)
    .slice(0, 20);

const uniqueSeoItems = (items = [], limit = 80) => {
  const seen = new Set();
  const output = [];
  items.forEach((item) => {
    const clean = String(item || '').replace(/\s+/g, ' ').trim();
    if (!clean) return;
    const key = clean.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    output.push(clean);
  });
  return output.slice(0, limit);
};

const buildArtistsDirectorySeo = (artists = []) => {
  const visibleArtists = artists.filter((artist) => artist?.is_verified !== false);
  const names = uniqueSeoItems(visibleArtists.map((artist) => artist.name), 18);
  const categories = uniqueSeoItems(visibleArtists.flatMap((artist) => [artist.category, artist.subcategory]), 24);
  const cities = uniqueSeoItems(visibleArtists.map((artist) => artist.city || artist.state), 18);
  const categoryCityTerms = uniqueSeoItems(
    visibleArtists.flatMap((artist) => {
      const category = artist.category || artist.subcategory;
      const city = artist.city || artist.state;
      return category && city ? [`${category} in ${city}`, `hire ${category} in ${city}`, `book ${category} in ${city}`] : [];
    }),
    30
  );
  const platformTerms = [
    'KalaManch artists',
    'Kalamanch.art',
    'verified Indian artists',
    'hire artists India',
    'book artists India',
    'artist portfolios India',
    'creative talent India',
    'Indian singers',
    'Indian dancers',
    'Indian painters',
    'Indian poets',
    'folk artists India',
    'event artists India',
    'collaborators for artists',
    'artist discovery platform',
  ];
  const descriptionParts = [
    visibleArtists.length ? `Browse ${visibleArtists.length} verified KalaManch artists` : 'Browse verified KalaManch artists',
    categories.length ? `including ${categories.slice(0, 8).join(', ')}` : 'across Indian creative forms',
    cities.length ? `from ${cities.slice(0, 8).join(', ')}` : 'across India',
    names.length ? `featuring ${names.slice(0, 5).join(', ')}` : '',
    'with profiles, portfolios, videos, PMB enquiries, and collaboration options.',
  ].filter(Boolean);

  return {
    title: 'Discover Verified Indian Artists | KalaManch.art',
    description: descriptionParts.join(' ').slice(0, 260),
    keywords: uniqueSeoItems([
      ...names,
      ...categories,
      ...cities,
      ...categoryCityTerms,
      ...platformTerms,
    ], 90).join(', ').slice(0, 700),
  };
};

const isSocietyEventArtist = (artist = {}) => {
  const text = [
    artist.smart_fit,
    artist.bio,
    artist.category,
    artist.subcategory,
    artist.native_bio,
    artist.native_language_badge,
    ...(Array.isArray(artist.seasonal_availability) ? artist.seasonal_availability : []),
  ].filter(Boolean).join(' ').toLowerCase();
  return SOCIETY_EVENT_TERMS.some((term) => text.includes(term));
};

const Artists = () => {
  const { isHindi } = useLanguage();
  const { artist: currentArtist } = useArtistAuth();
  const pageContent = usePageContent('artists');
  const [activeFilter, setActiveFilter] = useState('All');
  const [activeGroup, setActiveGroup] = useState('all');
  const [activeMainCategory, setActiveMainCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [nearMeOnly, setNearMeOnly] = useState(false);
  const [societyEventsOnly, setSocietyEventsOnly] = useState(false);
  const [userCoords, setUserCoords] = useState(null);
  const [nearMeStatus, setNearMeStatus] = useState('');
  const [showTalentMap, setShowTalentMap] = useState(false);
  const [artists, setArtists] = useState([]);
  const [loadingArtists, setLoadingArtists] = useState(true);
  const [artistsError, setArtistsError] = useState('');
  const [loadAttempt, setLoadAttempt] = useState(0);
  usePrerenderReady(loadingArtists);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const controller = new AbortController();
    setLoadingArtists(true);
    setArtistsError('');
    const fetchArtists = async () => {
      try {
        const response = await axios.get(`${API}/artists/all`, { timeout: 45000, signal: controller.signal });
        if (!Array.isArray(response.data?.artists)) throw new Error('Invalid artist catalogue response');
        if (!controller.signal.aborted) setArtists(response.data.artists);
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Failed to fetch artists:', error);
        setArtistsError('We could not load the artists. Please try again.');
      } finally {
        if (!controller.signal.aborted) setLoadingArtists(false);
      }
    };

    fetchArtists();
    return () => controller.abort();
  }, [loadAttempt]);

  const handleFieldUnlocked = async (artistId) => {
    try {
      const response = await axios.get(`${API}/artists/${artistId}`);
      setArtists((prev) => prev.map((artist) => artist.id === artistId ? response.data : artist));
    } catch (error) {
      console.error('Failed to refresh artist data:', error);
    }
  };

  const groupIds = {"Visual Arts":"visual-media","Music":"music-vocals","Dance":"dance","Acting & Theatre":"acting-performance","Literary Arts":"writing","Photography & Film":"photography-film","Crafts & Traditional Arts":"folk-cultural","Live Entertainment":"events-stage","Content Creation & Influencing":"influencer-creator","Modelling & Fashion":"modelling-fashion","Design & Creative Services":"design-creative","Beauty & Body Arts":"beauty-body","Arts Education & Cultural Practice":"education-culture","Culinary & Decorative Arts":"culinary-decorative"};
  const categoryGroups = [
    { id: 'all', name: 'All Categories', emoji: '🎨', categories: [], examples: ['All artists'] },
    ...Object.entries(taxonomy.categories).map(([name, forms]) => ({
      id: groupIds[name], name, emoji: '🎨',
      categories: [name, ...Object.entries(taxonomy.legacyGroups).filter(([, group]) => group === name).map(([legacy]) => legacy)],
      examples: Object.keys(forms),
    })),
  ];

  const getFilteredCategories = () => {
    const scopedArtists = artists.filter((artist) => artistMatchesMainCategory(artist, activeMainCategory));
    const categories = uniqueSorted([...scopedArtists.flatMap(getArtistSubcategories), ...(activeFilter !== 'All' ? [activeFilter] : [])]);
    if (activeGroup === 'all') return ['All', ...categories];

    const group = categoryGroups.find((item) => item.id === activeGroup);
    const groupTerms = new Set(getGroupTerms(group).map(normalizeArtKey));
    const availableCategories = categories.filter((category) => groupTerms.has(normalizeArtKey(category)));
    return ['All', ...availableCategories];
  };

  useEffect(() => {
    const queryParam = searchParams.get('q');
    const groupParam = searchParams.get('group');
    const mainParam = searchParams.get('main');
    const categoryParam = searchParams.get('category');

    if (queryParam) {
      setSearchQuery(queryParam);
    }

    if (groupParam && CATEGORY_GROUP_IDS.includes(groupParam)) {
      setActiveGroup(groupParam);
    }

    if (mainParam) {
      const liveMainCategories = uniqueSorted(artists.flatMap(getArtistMainCategories));
      const matchedMainCategory = liveMainCategories.find((category) => slugifyFilter(category) === mainParam);
      if (matchedMainCategory) {
        setActiveMainCategory(matchedMainCategory);
      }
    }

    if (categoryParam) {
      const liveCategories = uniqueSorted([...artists.flatMap(getArtistArtValues), ...taxonomyEntries.map(entry => entry.label)]);
      const matchedCategory = liveCategories.find((category) => slugifyFilter(category) === categoryParam);
      if (matchedCategory) {
        setActiveFilter(matchedCategory);
      }
    }
  }, [artists, searchParams]);

  const updateCategoryUrl = ({ groupId = activeGroup, mainCategory = activeMainCategory, category = activeFilter } = {}) => {
    const nextParams = {};
    if (groupId && groupId !== 'all') {
      nextParams.group = groupId;
    }
    if (mainCategory && mainCategory !== 'All') {
      nextParams.main = slugifyFilter(mainCategory);
    }
    if (category && category !== 'All') {
      nextParams.category = slugifyFilter(category);
    }
    setSearchParams(nextParams, { replace: true });
  };

  const clearDiscoveryFilters = () => {
    setSearchQuery('');
    setSelectedRegion('');
    setActiveGroup('all');
    setActiveMainCategory('All');
    setActiveFilter('All');
    setSocietyEventsOnly(false);
    updateCategoryUrl({ groupId: 'all', mainCategory: 'All', category: 'All' });
  };

  const talentRegions = Object.values(
    artists.reduce((regions, artist) => {
      const state = String(artist.state || '').trim();
      const city = String(artist.city || '').trim();
      const key = state || city;
      if (!key) return regions;
      if (!regions[key]) {
        regions[key] = {
          key,
          state,
          count: 0,
          verified: 0,
          cities: new Set(),
          categories: new Set(),
          languages: new Set(),
        };
      }
      regions[key].count += 1;
      if (artist.is_verified) regions[key].verified += 1;
      if (city) regions[key].cities.add(city);
      if (artist.category) regions[key].categories.add(artist.category);
      if (artist.native_language) regions[key].languages.add(artist.native_language);
      return regions;
    }, {})
  )
    .map((region) => ({
      ...region,
      cities: Array.from(region.cities).sort(),
      categories: Array.from(region.categories).sort(),
      languages: Array.from(region.languages).sort(),
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  const applyRegionFilter = (region) => {
    const regionLabel = region.state || region.key;
    setSelectedRegion(regionLabel);
    setSearchQuery(regionLabel);
    setNearMeOnly(false);
    setNearMeStatus('');
  };

  const nearMeCity = (currentArtist?.city || '').trim().toLowerCase();
  const nearMeReference = userCoords || {
    latitude: currentArtist?.latitude,
    longitude: currentArtist?.longitude,
  };
  const searchIntent = parseArtistSearchQuery(searchQuery);
  const hasUsableNearMeReference = isUsableIndiaCoordinate(
    toNumber(nearMeReference?.latitude),
    toNumber(nearMeReference?.longitude),
  );

  const handleNearMeToggle = () => {
    if (nearMeOnly) {
      setNearMeOnly(false);
      setNearMeStatus('');
      return;
    }
    setNearMeOnly(true);
    if (nearMeCity) {
      setNearMeStatus(`Showing artists near ${currentArtist.city}.`);
    }
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserCoords({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
          setNearMeStatus('Showing artists closest to your current location.');
        },
        () => {
          setNearMeStatus(nearMeCity ? `Showing artists from ${currentArtist.city}.` : 'Add your city in My Account for better Near Me results.');
        },
        { enableHighAccuracy: false, timeout: 7000, maximumAge: 300000 }
      );
    } else if (!nearMeCity) {
      setNearMeStatus('Location is not available on this browser. Add your city in My Account.');
    }
  };

  const filteredArtists = artists.map((artist) => ({
    ...artist,
    distance_km: (nearMeOnly || searchIntent.nearMe) && hasUsableNearMeReference ? getDistanceKm(nearMeReference, artist) : null,
  })).filter((artist) => {
    if (searchIntent.tokens.length > 0) {
      const searchableText = [
        artist.name,
        artist.category,
        artist.subcategory,
        artist.city,
        artist.state,
        artist.bio,
        artist.native_bio,
        artist.native_language,
        artist.native_language_badge,
        ...(Array.isArray(artist.seasonal_availability) ? artist.seasonal_availability : []),
      ].filter(Boolean).join(' ').toLowerCase();
      if (!searchIntent.tokens.some((token) => searchableText.includes(token))) return false;
    }
    if (searchIntent.location) {
      const locationText = [
        artist.city,
        artist.state,
        artist.district,
        artist.country,
      ].filter(Boolean).join(' ').toLowerCase();
      if (!locationText.includes(searchIntent.location)) return false;
    }
    if (selectedRegion) {
      const region = selectedRegion.toLowerCase();
      const artistState = String(artist.state || '').trim().toLowerCase();
      const artistCity = String(artist.city || '').trim().toLowerCase();
      if (artistState !== region && artistCity !== region) return false;
    }
    if (!artistMatchesArtFilter(artist, activeFilter)) return false;
    if (!artistMatchesMainCategory(artist, activeMainCategory)) return false;
    if (activeGroup !== 'all') {
      const group = categoryGroups.find((item) => item.id === activeGroup);
      if (!artistMatchesGroup(artist, group)) return false;
    }
    if (societyEventsOnly && !isSocietyEventArtist(artist)) return false;
    if (nearMeOnly || searchIntent.nearMe) {
      if (artist.distance_km !== null) return artist.distance_km <= 250;
      if (nearMeCity) return String(artist.city || '').trim().toLowerCase() === nearMeCity;
      return searchIntent.nearMe && !nearMeOnly;
    }
    return true;
  }).sort((artistA, artistB) => {
    if (nearMeOnly) {
      const distanceA = artistA.distance_km ?? Number.MAX_SAFE_INTEGER;
      const distanceB = artistB.distance_km ?? Number.MAX_SAFE_INTEGER;
      if (distanceA !== distanceB) return distanceA - distanceB;
    }
    const rankDifference = getDiscoveryRank(artistB) - getDiscoveryRank(artistA);
    if (rankDifference !== 0) return rankDifference;
    return getFreshnessTime(artistB) - getFreshnessTime(artistA);
  });

  const totalArtistsCount = filteredArtists.length;
  const verifiedArtistsCount = artists.filter((artist) => artist.is_verified).length;
  const citiesCount = new Set(artists.map((artist) => artist.city).filter(Boolean)).size;
  const categoriesCount = new Set(artists.flatMap(getArtistArtValues)).size;
  const managedSeasonalThemes = useMemo(
    () => parseSeasonalThemes(pageContent.seasonal_themes),
    [pageContent.seasonal_themes]
  );
  const seasonalThemes = managedSeasonalThemes.length > 0 ? managedSeasonalThemes : SEASONAL_THEMES;
  const activeGroupDetails = categoryGroups.find((group) => group.id === activeGroup) || categoryGroups[0];
  const groupArtistCount = (group) => {
    const scopedArtists = artists.filter((artist) => artistMatchesMainCategory(artist, activeMainCategory));
    if (group.id === 'all') return scopedArtists.length;
    return scopedArtists.filter((artist) => artistMatchesGroup(artist, group)).length;
  };
  const artistsDirectorySeo = useMemo(() => buildArtistsDirectorySeo(artists), [artists]);

  return (
    <div className="min-h-screen bg-gray-50 pt-[4.75rem] sm:pt-[5.25rem]" data-testid="artists-page">
      <SEO
        title={artistsDirectorySeo.title}
        description={artistsDirectorySeo.description}
        keywords={artistsDirectorySeo.keywords}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        <section className="relative mb-6 overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#fff7ed] via-white to-[#f5f0ff] p-4 shadow-xl ring-1 ring-orange-100 sm:p-6 lg:p-7" data-testid="artists-discovery-hero">
          <div className="absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[#FF6F00]/15 blur-3xl" />
          <div className="absolute -bottom-24 left-10 h-64 w-64 rounded-full bg-[#00BFA5]/15 blur-3xl" />
          <div className="relative grid gap-6 lg:grid-cols-[1.2fr_0.8fr] lg:items-end">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF6F00]">
                Verified talent discovery
              </p>
              <h1 className="launch-heading mt-2 max-w-4xl text-3xl font-bold gradient-text sm:text-4xl lg:text-5xl" data-testid="artists-page-heading">
                {isHindi ? '??????????? ?????? ?????' : 'Discover Talented Artists'}
              </h1>
              <p className="launch-copy mt-3 max-w-3xl text-base leading-7 text-gray-600 sm:text-lg" data-testid="artists-page-description">
                {isHindi ? '????? ?????? ??? ????????? ?? ??????????? ?? ?????? Kala Manch ???????? ?? ??????' : 'Explore verified Indian artists by art form, city, speciality, and portfolio readiness.'}
              </p>
              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Try tattoo artist, tattoo artist in Mumbai, singer near me..."
                  className="w-full rounded-full border border-orange-100 bg-white px-5 py-3.5 text-gray-800 shadow-sm outline-none transition focus:border-[#FF6F00] focus:ring-4 focus:ring-orange-100"
                  data-testid="artist-search-input"
                />
                {(searchQuery || selectedRegion || activeGroup !== 'all' || activeMainCategory !== 'All' || activeFilter !== 'All' || societyEventsOnly) && (
                  <button
                    type="button"
                    onClick={clearDiscoveryFilters}
                    className="rounded-full border border-orange-200 bg-white px-6 py-3.5 font-bold text-[#FF6F00] shadow-sm transition hover:bg-orange-50"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs font-semibold text-gray-500">
                Search understands art forms and places, for example: tattoo artist in Mumbai, dancer near me, singer in Delhi.
              </p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handleNearMeToggle}
                  className={`rounded-full px-5 py-3 text-sm font-bold shadow-sm transition ${nearMeOnly ? 'bg-[#111827] text-white' : 'border border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50'}`}
                >
                  {nearMeOnly ? 'Near Me On' : 'Near Me'}
                </button>
                {talentRegions.length > 0 && !showTalentMap && (
                  <button
                    type="button"
                    onClick={() => setShowTalentMap(true)}
                    className="rounded-full border border-emerald-200 bg-white px-5 py-3 text-sm font-bold text-emerald-700 shadow-sm transition hover:bg-emerald-50"
                  >
                    Bharat Talent Map
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setSocietyEventsOnly((value) => !value)}
                  className={`rounded-full px-5 py-3 text-sm font-bold shadow-sm transition ${societyEventsOnly ? 'bg-[#4A148C] text-white' : 'border border-purple-200 bg-white text-[#4A148C] hover:bg-purple-50'}`}
                >
                  {societyEventsOnly ? 'Society Events On' : 'Society Events'}
                </button>
                {nearMeStatus && <p className="text-sm font-semibold text-gray-600">{nearMeStatus}</p>}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 rounded-[1.75rem] bg-white/80 p-4 shadow-lg ring-1 ring-orange-100 backdrop-blur">
              <div className="rounded-2xl bg-orange-50 p-4 text-center">
                <p className="text-2xl font-bold text-[#FF6F00]">{verifiedArtistsCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-600">Verified</p>
              </div>
              <div className="rounded-2xl bg-teal-50 p-4 text-center">
                <p className="text-2xl font-bold text-[#008C7A]">{citiesCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-600">Cities</p>
              </div>
              <div className="rounded-2xl bg-purple-50 p-4 text-center">
                <p className="text-2xl font-bold text-[#4A148C]">{categoriesCount}</p>
                <p className="mt-1 text-xs font-semibold text-gray-600">Art forms</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-5 rounded-[1.5rem] border border-orange-100 bg-white p-3 shadow-sm sm:p-4" data-testid="seasonal-discovery-strip">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF6F00]">Seasonal discovery</p>
              <p className="mt-1 text-sm font-semibold text-gray-700">Find artists by festivals, weddings, school/college days, society programs, and cultural seasons.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {seasonalThemes.map((theme) => (
                <button
                  key={theme.label}
                  type="button"
                  onClick={() => {
                    setSearchQuery(theme.query);
                    setSocietyEventsOnly(true);
                  }}
                  className="rounded-full bg-orange-50 px-3 py-2 text-xs font-extrabold text-[#C45A00] transition hover:bg-orange-100"
                >
                  {theme.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        <section className="mb-5 rounded-[1.5rem] border border-purple-100 bg-white p-3 shadow-sm sm:p-4" data-testid="mood-discovery-strip">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#4A148C]">Mood-based discovery</p>
              <p className="mt-1 text-sm font-semibold text-gray-700">Search by vibe, not only category: peaceful, energetic, patriotic, spiritual, romantic, folk, youth, comedy, and wedding mood.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {MOOD_DISCOVERY.map((mood) => (
                <button
                  key={mood.label}
                  type="button"
                  onClick={() => {
                    setSearchQuery(mood.query);
                    setSocietyEventsOnly(false);
                    setSelectedRegion('');
                  }}
                  className="rounded-full bg-purple-50 px-3 py-2 text-xs font-extrabold text-[#4A148C] transition hover:bg-purple-100"
                >
                  {mood.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {talentRegions.length > 0 && !showTalentMap && (
          <section className="mb-5 rounded-[1.5rem] border border-emerald-100 bg-white p-3 shadow-sm sm:p-4" data-testid="bharat-talent-map-collapsed">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Bharat Talent Map</p>
                <p className="mt-1 text-sm font-semibold text-gray-700">
                  {talentRegions.length} active region{talentRegions.length !== 1 ? 's' : ''}. Open only when you want state/city discovery.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowTalentMap(true)}
                className="rounded-full bg-emerald-50 px-5 py-2.5 text-sm font-extrabold text-emerald-800 transition hover:bg-emerald-100"
              >
                Open Map
              </button>
            </div>
          </section>
        )}

        {talentRegions.length > 0 && showTalentMap && (
          <section className="relative mb-6 overflow-hidden rounded-[2rem] border border-emerald-100 bg-white shadow-xl" data-testid="bharat-talent-map">
            <button
              type="button"
              onClick={() => setShowTalentMap(false)}
              className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white text-xl font-black leading-none text-emerald-900 shadow-lg ring-1 ring-emerald-100 transition hover:bg-emerald-50 sm:right-4 sm:top-4"
              aria-label="Close Bharat Talent Map"
              title="Close map"
            >
              &times;
            </button>
            <div className="grid gap-0 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="bg-gradient-to-br from-[#07111F] via-[#123A38] to-[#FF6F00] p-5 text-white sm:p-6">
                <div className="max-w-[calc(100%-3rem)]">
                  <p className="text-xs font-bold uppercase tracking-[0.28em] text-emerald-100">Bharat Talent Map</p>
                  <h2 className="mt-3 text-2xl font-black leading-tight sm:text-3xl">Discover creators by state and city</h2>
                </div>
                <p className="mt-3 text-sm leading-6 text-white/80">
                  Tap a region to see creators from that area. Search also supports native language terms like ???, ????, tabla, bhajan, folk, singer, and more.
                </p>
                {selectedRegion && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedRegion('');
                      setSearchQuery('');
                    }}
                    className="mt-5 rounded-full bg-white px-5 py-2.5 text-sm font-extrabold text-[#0F766E]"
                  >
                    Clear {selectedRegion}
                  </button>
                )}
              </div>

              <div className="grid gap-3 bg-[#f7fffb] p-4 sm:grid-cols-2 lg:p-5">
                {talentRegions.map((region) => (
                  <button
                    key={region.key}
                    type="button"
                    onClick={() => applyRegionFilter(region)}
                    className={`rounded-[1.35rem] border p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg ${
                      selectedRegion === (region.state || region.key)
                        ? 'border-[#0F766E] bg-emerald-50'
                        : 'border-emerald-100 bg-white'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-lg font-black text-gray-950">{region.state || region.key}</p>
                        <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-emerald-700">
                          {region.count} artist{region.count !== 1 ? 's' : ''}, {region.verified} verified
                        </p>
                      </div>
                      <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-extrabold text-[#FF6F00]">
                        Explore
                      </span>
                    </div>
                    <p className="mt-3 line-clamp-1 text-sm font-semibold text-gray-700">
                      {region.cities.slice(0, 4).join(', ') || 'City details updating'}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {region.categories.slice(0, 3).map((category) => (
                        <span key={category} className="rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold text-gray-700">
                          {category}
                        </span>
                      ))}
                      {region.languages.slice(0, 2).map((language) => (
                        <span key={language} className="rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-bold text-emerald-800">
                          {language}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        <div className="mb-4 rounded-[1.5rem] border border-orange-100 bg-white p-3 shadow-sm sm:p-4">
          <div className="mb-4"><ArtCategorySearch allowCustom={false} selected={activeFilter === 'All' ? [] : [activeFilter]} onSelect={({ label }) => {
            setActiveGroup('all');
            setActiveMainCategory('All');
            setActiveFilter(label);
            setSearchQuery('');
            setSelectedRegion('');
            updateCategoryUrl({ groupId: 'all', mainCategory: 'All', category: label });
          }} /></div>
          <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#00BFA5]">Curated discovery</p>
              <h3 className="text-xl font-bold text-gray-900">{isHindi ? '????? ??? ?????? ?? ?????' : 'Browse Art Category'}</h3>
            </div>
            <p className="text-xs font-semibold text-gray-500">Choose filters, then keep browsing artists below.</p>
          </div>

          <div className="grid gap-3 lg:grid-cols-[1fr_1fr_auto] lg:items-end">
            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Main category</span>
              <select
                value={activeGroup}
                onChange={(event) => {
                  const nextGroup = event.target.value;
                  setActiveGroup(nextGroup);
                  setActiveMainCategory('All');
                  setActiveFilter('All');
                  updateCategoryUrl({ groupId: nextGroup, mainCategory: 'All', category: 'All' });
                }}
                className="mt-1 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-[#FF6F00] focus:ring-2 focus:ring-orange-100"
                data-testid="category-group-select"
              >
                {categoryGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.emoji} {group.name} ({loadingArtists || artistsError ? '…' : groupArtistCount(group)})
                  </option>
                ))}
              </select>
            </label>

            <label className="block">
              <span className="text-xs font-bold uppercase tracking-[0.16em] text-gray-500">Subcategory</span>
              <select
                value={activeFilter}
                onChange={(event) => {
                  const nextFilter = event.target.value;
                  setActiveFilter(nextFilter);
                  updateCategoryUrl({ groupId: activeGroup, mainCategory: activeMainCategory, category: nextFilter });
                }}
                className="mt-1 w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm font-bold text-gray-800 outline-none focus:border-[#00BFA5] focus:ring-2 focus:ring-emerald-100"
                data-testid="category-filter-select"
              >
                {getFilteredCategories().map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </label>

            {(activeGroup !== 'all' || activeMainCategory !== 'All' || activeFilter !== 'All') && (
              <button
                type="button"
                onClick={() => {
                  setActiveGroup('all');
                  setActiveMainCategory('All');
                  setActiveFilter('All');
                  updateCategoryUrl({ groupId: 'all', mainCategory: 'All', category: 'All' });
                }}
                className="rounded-full border border-orange-200 bg-white px-5 py-3 text-sm font-bold text-[#FF6F00] shadow-sm transition hover:bg-orange-50"
              >
                Clear category
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap gap-2" data-testid="category-groups">
            {categoryGroups.slice(1).map((group) => (
              <button
                key={group.id}
                type="button"
                data-testid={`group-button-${group.id}`}
                onClick={() => {
                  setActiveGroup(group.id);
                  setActiveMainCategory('All');
                  setActiveFilter('All');
                  updateCategoryUrl({ groupId: group.id, mainCategory: 'All', category: 'All' });
                }}
                className={`rounded-full px-3 py-1.5 text-xs font-bold transition ${activeGroup === group.id ? 'bg-[#4A148C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-[#FF6F00]'}`}
              >
                {group.emoji} {group.name}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-5 text-center" data-testid="results-count">
          {loadingArtists || artistsError ? (
            <p className="text-gray-600 text-base" role="status">{loadingArtists ? 'Loading artists…' : 'Artist count unavailable'}</p>
          ) : (
          <p className="text-gray-600 text-base">
            {isHindi ? '????? ?? ??? ??? ' : 'Showing '}<span className="font-semibold text-[#FF6F00]">{totalArtistsCount}</span>{isHindi ? ' ??????' : ` artist${totalArtistsCount !== 1 ? 's' : ''}`}
            {filteredArtists.length > 0 && <span className="text-sm text-green-600 ml-2">{isHindi ? '(???? ???????????)' : '(live registrations)'}</span>}
          </p>
          )}
        </div>

        {loadingArtists ? (
          <div className="text-center py-20 text-gray-500">{isHindi ? '?????? ??? ?? ??? ???...' : 'Loading artists...'}</div>
        ) : artistsError ? (
          <div className="rounded-[2rem] border border-orange-100 bg-white p-10 text-center shadow-lg" role="alert" data-testid="artists-load-error">
            <h3 className="text-2xl font-bold text-gray-900">Artists are temporarily unavailable</h3>
            <p className="mt-3 text-gray-600">{artistsError}</p>
            <button type="button" onClick={() => setLoadAttempt(attempt => attempt + 1)} className="btn-primary mt-6">Retry loading artists</button>
          </div>
        ) : filteredArtists.length > 0 ? (
          <div className="mb-12">
            <div className="flex items-center justify-center gap-2 mb-6">
              <h3 className="text-2xl font-bold gradient-text">{isHindi ? 'Kala Manch ?? ??????' : 'Artists on Kala Manch'}</h3>
              <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-medium">Verified</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredArtists.map((artist) => (
                <RegisteredArtistCard key={artist.id} artist={artist} showDistance={nearMeOnly} onFieldUnlocked={handleFieldUnlocked} />
              ))}
            </div>
          </div>
        ) : (
          <div className="rounded-[2rem] border border-orange-100 bg-white p-10 text-center shadow-lg" data-testid="no-results">
            <p className="text-5xl">??</p>
            <h3 className="mt-4 text-2xl font-bold text-gray-900">{isHindi ? '?? ?????? ??? ??? ?????? ???? ?????' : 'No artists found yet'}</h3>
            <p className="mx-auto mt-3 max-w-xl text-gray-500">
              Try another category, clear filters, or invite artists from this field to join KalaManch.
            </p>
            <button
              type="button"
              onClick={() => {
                clearDiscoveryFilters();
              }}
              className="btn-primary mt-6"
            >
              Show all artists
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Artists;
